import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import Modal from "@/components/Modal";
import { useInquiry } from "@/context/InquiryContext";
import useProducts from "@/hooks/useProducts";
import { submitQuote } from "@/lib/api";
import { btnPrimary, inputCls, labelCls } from "@/lib/site";

export default function QuoteModal() {
  const { quote, closeQuote } = useInquiry();
  const { data: products } = useProducts();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (quote.open)
      setForm({
        product_name: quote.product || "",
        name: "",
        company: "",
        email: "",
        phone: "",
        quantity: "",
        message: "",
      });
  }, [quote.open, quote.product]);

  const options = useMemo(() => {
    const names = (products || []).map((p) => p.name);
    if (form?.product_name && !names.includes(form.product_name)) return [form.product_name, ...names];
    return names;
  }, [products, form?.product_name]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await submitQuote(form);
      toast.success(`Price enquiry received — Ref ${res.ref}`, {
        description: "We'll send an indicative quotation within one working day.",
      });
      closeQuote();
    } catch {
      toast.error("Could not submit your enquiry. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={quote.open}
      onClose={closeQuote}
      testId="quote-modal"
      title="Ask for Price"
      subtitle="Quick price query — no commitment to order."
    >
      {form && (
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className={labelCls}>Product *</label>
            <select
              required
              data-testid="quote-modal-product-select"
              className={inputCls}
              value={form.product_name}
              onChange={set("product_name")}
            >
              <option value="" disabled>
                Select a product
              </option>
              {options.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Your Name *</label>
              <input required data-testid="quote-modal-name-input" className={inputCls} value={form.name} onChange={set("name")} />
            </div>
            <div>
              <label className={labelCls}>Phone *</label>
              <input required data-testid="quote-modal-phone-input" className={inputCls} placeholder="+91…" value={form.phone} onChange={set("phone")} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Email *</label>
              <input type="email" required data-testid="quote-modal-email-input" className={inputCls} value={form.email} onChange={set("email")} />
            </div>
            <div>
              <label className={labelCls}>Company</label>
              <input data-testid="quote-modal-company-input" className={inputCls} value={form.company} onChange={set("company")} />
            </div>
          </div>
          <div>
            <label className={labelCls}>Approx. Quantity</label>
            <input data-testid="quote-modal-quantity-input" className={inputCls} placeholder="e.g. 2 units" value={form.quantity} onChange={set("quantity")} />
          </div>
          <div>
            <label className={labelCls}>Message</label>
            <textarea rows={3} data-testid="quote-modal-message-input" className={inputCls} placeholder="Configuration, timeline, application…" value={form.message} onChange={set("message")} />
          </div>
          <button data-testid="quote-modal-submit-btn" className={`${btnPrimary} w-full`} disabled={busy}>
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            {busy ? "Sending…" : "Ask for Price"}
          </button>
          <p className="text-center text-[11px] text-slate-400">
            You'll receive a confirmation email with your reference number.
          </p>
        </form>
      )}
    </Modal>
  );
}
